import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { X, Play } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import GallerySkeleton from "@/components/GallerySkeleton";
import { supabase } from "@/integrations/supabase/client";
import { staticMedia, buildAltText, type MediaItem } from "@/data/galleryMedia";

const Realizacje = () => {
  const [selected, setSelected] = useState<number | null>(null);
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [media, setMedia] = useState<MediaItem[]>(staticMedia.filter((m) => m.type === "video"));
  const [galleryLoading, setGalleryLoading] = useState(true);

  useEffect(() => {
    const fetchCmsMedia = async () => {
      const { data } = await supabase
        .from("gallery_media")
        .select("*")
        .order("position", { ascending: true });
      if (data && data.length > 0) {
        const cmsItems: MediaItem[] = data
          .map((item, idx) => ({
            type: item.type as "image" | "video",
            src: item.url,
            alt: buildAltText(item, idx),
            poster: item.poster_url || undefined,
            mirror: item.mirror,
          }))
          .filter((item) => item.type === "video");
        const staticVideos = staticMedia.filter((m) => m.type === "video");
        setMedia([...cmsItems, ...staticVideos]);
      } else {
        setMedia(staticMedia.filter((m) => m.type === "video"));
      }
      setGalleryLoading(false);
    };
    fetchCmsMedia();
  }, []);

  useEffect(() => {
    if (selected !== null && media[selected]?.type === "video" && videoRef.current) {
      videoRef.current.play();
    }
  }, [selected, media]);

  // Lazy-load video sources when near the viewport (load only, don't play all at once)
  useEffect(() => {
    if (!gridRef.current) return;
    const videos = gridRef.current.querySelectorAll("video[data-src]");
    if (!videos.length) return;

    // Preload sources when near the viewport so playback starts instantly.
    const preloadObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          if (entry.isIntersecting && !video.src && video.dataset.src) {
            video.src = video.dataset.src;
            video.load();
          }
        });
      },
      { rootMargin: "400px", threshold: 0.01 }
    );

    // Auto-play only fully visible videos and pause the rest to keep playback smooth.
    const playObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) {
            if (!video.src && video.dataset.src) {
              video.src = video.dataset.src;
              video.load();
            }
            video.play().catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { threshold: 0.5 }
    );

    videos.forEach((v) => {
      preloadObserver.observe(v);
      playObserver.observe(v);
    });
    return () => {
      preloadObserver.disconnect();
      playObserver.disconnect();
    };
  }, [media]);

  const handlePreview = (video: HTMLVideoElement) => {
    if (!video.src && video.dataset.src) {
      video.src = video.dataset.src;
      video.load();
    }
    video.play().catch(() => {});
  };

  const selectedItem = selected !== null ? media[selected] : null;

  return (
    <Layout breadcrumbTitle={t("realizacje.title")}>
      <SEOHead
        title={t("realizacje.seoTitle")}
        description={t("realizacje.seoDescription")}
      />
      <section className="py-12 md:py-20 lg:py-28">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-14">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">
              {t("realizacje.title")}
            </h1>
            <p className="text-muted-foreground text-sm md:text-lg max-w-2xl mx-auto">
              {t("realizacje.subtitle")}
            </p>
          </div>

          {galleryLoading ? (
            <GallerySkeleton />
          ) : (
            <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {media.map((item, i) => (
                <button
                  key={i}
                  onClick={() => setSelected(i)}
                  onMouseEnter={(e) => handlePreview(e.currentTarget.querySelector("video")!)}
                  onMouseLeave={(e) => e.currentTarget.querySelector("video")!.pause()}
                  className="relative overflow-hidden rounded-xl aspect-video group cursor-pointer bg-muted"
                >
                  <video
                    data-src={item.src}
                    poster={item.poster}
                    muted
                    playsInline
                    loop
                    preload="metadata"
                    className={`w-full h-full object-cover transform-gpu [backface-visibility:hidden] ${item.mirror ? "-scale-x-100" : ""}`}
                  />
                  <div className="absolute inset-0 bg-navy-dark/30 group-hover:bg-navy-dark/50 transition-colors flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-accent/90 flex items-center justify-center opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all">
                      <Play className="w-6 h-6 text-accent-foreground ml-1" fill="currentColor" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {selectedItem && (
          <div
            className="fixed inset-0 z-50 bg-navy-dark/90 flex items-center justify-center p-4 animate-fade-in"
            onClick={() => setSelected(null)}
          >
            <button
              onClick={() => setSelected(null)}
              className="absolute top-4 right-4 text-primary-foreground/80 hover:text-primary-foreground z-10"
            >
              <X className="w-8 h-8" />
            </button>
            <video
              ref={videoRef}
              src={selectedItem.src}
              controls
              autoPlay
              muted
              loop
              className={`max-w-full max-h-[85vh] rounded-xl ${selectedItem.mirror ? "-scale-x-100" : ""}`}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        )}
      </section>
    </Layout>
  );
};

export default Realizacje;
